import { formatColumnValue, parseColumnFormat, type ColumnFormat } from "./columnFormat";
import type { ColumnBarFill } from "./tableMappingBuilder";

export type TableColumnDef = {
  header: string;
  field?: string;
  format?: ColumnFormat;
  subtotal?: boolean;
  barFill?: ColumnBarFill;
};

export type NormalizedTablePayload = {
  columns: TableColumnDef[];
  rows: Record<string, unknown>[];
  subtotals?: Record<string, number>;
};

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

function columnHeaderFromRecord(col: Record<string, unknown>): string {
  for (const key of ["header", "label", "title", "name", "field"]) {
    const v = col[key];
    if (v != null && String(v).trim() !== "") {
      return String(v).trim();
    }
  }
  return "";
}

function parseBarFill(raw: unknown): ColumnBarFill | undefined {
  if (!isRecord(raw)) return undefined;
  const mode = raw.mode != null ? String(raw.mode).trim() : undefined;
  const normalizedMode =
    mode === "positive" || mode === "negative" || mode === "both" ? mode : undefined;
  const color = raw.color != null ? String(raw.color).trim() : undefined;
  const showValue = raw.showValue === true || raw.showValue === false ? raw.showValue : undefined;
  const min = raw.min != null && Number.isFinite(Number(raw.min)) ? Number(raw.min) : undefined;
  const max = raw.max != null && Number.isFinite(Number(raw.max)) ? Number(raw.max) : undefined;
  if (!normalizedMode && !color && showValue === undefined && min === undefined && max === undefined) {
    return undefined;
  }
  return {
    ...(normalizedMode ? { mode: normalizedMode } : {}),
    ...(color ? { color } : {}),
    ...(showValue !== undefined ? { showValue } : {}),
    ...(min !== undefined ? { min } : {}),
    ...(max !== undefined ? { max } : {}),
  };
}

function parseColumnDef(col: unknown): TableColumnDef | null {
  if (typeof col === "string") {
    const header = col.trim();
    return header ? { header, field: header } : null;
  }
  if (!isRecord(col)) return null;
  const header = columnHeaderFromRecord(col);
  if (!header) return null;
  const field =
    col.field != null && String(col.field).trim() !== ""
      ? String(col.field).trim()
      : header;
  const format = parseColumnFormat(col.format);
  const subtotal = col.subtotal === true;
  const barFill = parseBarFill(col.barFill);
  return {
    header,
    field,
    ...(format ? { format } : {}),
    ...(subtotal ? { subtotal } : {}),
    ...(barFill ? { barFill } : {}),
  };
}

function parseSubtotals(raw: unknown): Record<string, number> | undefined {
  if (!isRecord(raw)) return undefined;
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw)) {
    const num = Number(value);
    if (Number.isFinite(num)) out[key] = num;
  }
  return Object.keys(out).length ? out : undefined;
}

export function getTableCellRaw(
  row: Record<string, unknown>,
  field: string,
  header: string
): unknown {
  return row[field] ?? row[header];
}

export function getTableCellDisplay(
  row: Record<string, unknown>,
  field: string,
  header: string,
  format?: ColumnFormat
): string {
  const value = getTableCellRaw(row, field, header);
  if (value == null) return "";
  return formatColumnValue(value, format);
}

/** Normalize widget table payload or legacy row arrays / chat HTML envelopes. */
export function normalizeTablePayload(result: unknown): NormalizedTablePayload | null {
  if (result == null) return null;

  if (typeof result === "string") {
    const trimmed = result.trim();
    if (trimmed.startsWith("<")) return null;
    try {
      return normalizeTablePayload(JSON.parse(trimmed));
    } catch {
      return null;
    }
  }

  if (isRecord(result) && typeof result.response === "string") {
    const inner = result.response.trim();
    if (inner.startsWith("<")) return null;
    try {
      return normalizeTablePayload(JSON.parse(inner));
    } catch {
      return null;
    }
  }

  if (isRecord(result) && Array.isArray(result.columns) && Array.isArray(result.rows)) {
    const columns = (result.columns as unknown[])
      .map(parseColumnDef)
      .filter((c): c is TableColumnDef => c != null);
    let rows = (result.rows as unknown[]).filter(isRecord) as Record<string, unknown>[];
    // Coerce array-shaped rows [[a,b], ...] using column fields.
    if (rows.length === 0 && (result.rows as unknown[]).length > 0) {
      rows = coerceArrayRows(result.rows as unknown[], columns);
    }
    // If the model left rows empty but OpenSearch aggregations are present, map buckets.
    if (rows.length === 0) {
      const fromAggs = rowsFromAggregations(result, columns);
      if (fromAggs.length) rows = fromAggs;
    }
    const subtotals = parseSubtotals(result.subtotals);
    if (columns.length) return { columns, rows, ...(subtotals ? { subtotals } : {}) };
  }

  // Aggregation-only payloads (no columns/rows yet)
  if (isRecord(result)) {
    const fromAggs = rowsFromAggregations(result, undefined);
    if (fromAggs.length) {
      const columns: TableColumnDef[] = [
        { header: "Saat", field: "key" },
        { header: "Kayıt sayısı", field: "doc_count" },
      ];
      return { columns, rows: fromAggs };
    }
  }

  if (Array.isArray(result) && result.length > 0 && isRecord(result[0])) {
    const rows = result.filter(isRecord) as Record<string, unknown>[];
    const keys = Object.keys(rows[0]);
    const columns = keys.map((k) => ({ header: k, field: k }));
    return { columns, rows };
  }

  return null;
}

function coerceArrayRows(
  rawRows: unknown[],
  columns: TableColumnDef[]
): Record<string, unknown>[] {
  if (!columns.length) return [];
  const out: Record<string, unknown>[] = [];
  for (const row of rawRows) {
    if (!Array.isArray(row)) continue;
    const obj: Record<string, unknown> = {};
    columns.forEach((col, i) => {
      const field = col.field || col.header;
      obj[field] = row[i];
    });
    out.push(obj);
  }
  return out;
}

function rowsFromAggregations(
  result: Record<string, unknown>,
  columns: TableColumnDef[] | undefined
): Record<string, unknown>[] {
  const aggs = (result.aggregations ?? result.aggs) as unknown;
  if (!isRecord(aggs)) return [];
  for (const value of Object.values(aggs)) {
    if (!isRecord(value) || !Array.isArray(value.buckets)) continue;
    const buckets = value.buckets as unknown[];
    if (!buckets.length) continue;
    const keyField =
      columns?.find((c) => /saat|hour|key|zaman|date|bucket/i.test(c.header) || /key/i.test(c.field || ""))
        ?.field ||
      columns?.[0]?.field ||
      "key";
    const countField =
      columns?.find((c) => /say|count|adet|kayıt|kayit/i.test(c.header) || /doc_count|count/i.test(c.field || ""))
        ?.field ||
      columns?.[1]?.field ||
      "doc_count";
    return buckets
      .filter(isRecord)
      .map((b) => {
        const key = b.key_as_string ?? b.key;
        const count = b.doc_count ?? b.count ?? 0;
        return {
          [keyField]: key,
          [countField]: count,
          key,
          key_as_string: b.key_as_string ?? key,
          doc_count: count,
        };
      });
  }
  return [];
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function cellValue(
  row: Record<string, unknown>,
  field: string,
  header: string,
  format?: ColumnFormat
): string {
  return escapeHtml(getTableCellDisplay(row, field, header, format));
}

/** Build HTML table from structured widget rows or return existing HTML string. */
export function buildTableHtml(result: unknown): string {
  if (typeof result === "string") {
    const trimmed = result.trim();
    if (trimmed.startsWith("<")) return trimmed;
    const normalized = normalizeTablePayload(result);
    if (normalized) return buildTableHtml(normalized);
    return "";
  }

  if (isRecord(result) && typeof result.response === "string" && result.response.trim().startsWith("<")) {
    return result.response;
  }

  const normalized = normalizeTablePayload(result);
  if (!normalized) return "";

  if (normalized.columns.length === 0) return "";

  const headerCells = normalized.columns
    .map((c) => `<th>${escapeHtml(c.header)}</th>`)
    .join("");

  if (normalized.rows.length === 0) {
    const colSpan = Math.max(normalized.columns.length, 1);
    return `<table><thead><tr>${headerCells}</tr></thead><tbody><tr><td colspan="${colSpan}" style="text-align:center;color:#666">—</td></tr></tbody></table>`;
  }

  const bodyRows = normalized.rows
    .map((row) => {
      const cells = normalized.columns
        .map((c) => {
          const field = c.field || c.header;
          return `<td>${cellValue(row, field, c.header, c.format)}</td>`;
        })
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  const subtotalRow =
    normalized.subtotals && Object.keys(normalized.subtotals).length
      ? `<tr>${normalized.columns
          .map((c) => {
            const field = c.field || c.header;
            const total = normalized.subtotals?.[field];
            const text = total != null ? escapeHtml(formatColumnValue(total, c.format)) : "";
            return `<td><strong>${text}</strong></td>`;
          })
          .join("")}</tr>`
      : "";

  return `<table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}${subtotalRow}</tbody></table>`;
}
